import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Button } from 'antd';
import { Row, Col } from 'react-bootstrap';
import { DeleteFilled, PlusOutlined } from '@ant-design/icons';
import { setPackingSupplies, setMovingSupplies } from './store/actionCreators';
import moment from 'moment';
import './index.css';

const OrderSummary = ({ deliveryWindows }) => {
  const dispatch = useDispatch();
  const {
    boxCount,
    boxProduct,
    packingSupplies,
    movingSupplies,
    deliveryExtraWork,
    pickupExtraWork,
    pricing,
    deliveryDetails,
    pickupDetails
  } = useSelector(state => state.alaCarte);

  const increaseQuantity = (index, type) => {
    if (type === 'PACKING') {
      const updated = [...packingSupplies];
      updated[index] = { ...updated[index], quantity: updated[index].quantity + 1 };
      dispatch(setPackingSupplies(updated));
    } else if (type === 'MOVING') {
      const updated = [...movingSupplies];
      updated[index] = { ...updated[index], quantity: updated[index].quantity + 1 };
      dispatch(setMovingSupplies(updated));
    }
  };

  const decreaseQuantity = (index, type) => {
    if (type === 'PACKING') {
      const updated = [...packingSupplies];
      if (updated[index].quantity > 1) {
        updated[index] = { ...updated[index], quantity: updated[index].quantity - 1 };
        dispatch(setPackingSupplies(updated));
      } else {
        // Remove item if quantity becomes 0
        updated.splice(index, 1);
        dispatch(setPackingSupplies(updated));
      }
    } else if (type === 'MOVING') {
      const updated = [...movingSupplies];
      if (updated[index].quantity > 1) {
        updated[index] = { ...updated[index], quantity: updated[index].quantity - 1 };
        dispatch(setMovingSupplies(updated));
      } else {
        // Remove item if quantity becomes 0
        updated.splice(index, 1);
        dispatch(setMovingSupplies(updated));
      }
    }
  };

  const getWindowDisplay = (windowId) => {
    if (!windowId || !deliveryWindows) return null;
    const window = deliveryWindows.find(w => w.id === windowId);
    return window ? window.display_value : null;
  };

  const getSortedWindows = (window1Id, window2Id) => {
    if (!window1Id || !window2Id || !deliveryWindows) return [window1Id, window2Id];

    const window1 = deliveryWindows.find(w => w.id === window1Id);
    const window2 = deliveryWindows.find(w => w.id === window2Id);

    if (!window1 || !window2) return [window1Id, window2Id];

    // Compare start times to sort chronologically
    const time1 = window1.start_time || '';
    const time2 = window2.start_time || '';

    // Sort by start time
    if (time1 < time2) {
      return [window1Id, window2Id];
    } else {
      return [window2Id, window1Id];
    }
  };

  return (
    <Card title="Order Summary" className="order-summary-card">
      <Row className="ml-0 mr-0">
        <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12}>
          <div className="summary-panel-content pl-0 pr-0 order-package">
            <p className="text-left mb-1 fw-600 package-title">Boxes</p>
            {boxCount > 0 ? (
              <>
                <Row className="pr-15p">
                  <Col xxl={9} xl={9} lg={9} md={9} sm={9} xs={9}>
                    <p className="text-left mb-1 package-desc">
                      {boxCount} {boxCount === 1 ? 'Box' : 'Boxes'}
                      {pricing.remainingWeeks > 0 && pricing.boxPricePerWeek > 0 && (
                        <span style={{ fontSize: '11px', display: 'block', color: '#666', marginTop: '2px' }}>
                          ${pricing.boxPricePerWeek.toFixed(2)}/box/week × {pricing.remainingWeeks} {pricing.remainingWeeks === 1 ? 'week' : 'weeks'}
                        </span>
                      )}
                    </p>
                  </Col>
                  <Col xxl={3} xl={3} lg={3} md={3} sm={3} xs={3}>
                    <p className="text-left mb-1 package-desc">
                      ${pricing?.boxesSubtotal?.toFixed(2) || '0.00'}
                    </p>
                  </Col>
                </Row>
                {pricing.remainingWeeks > 0 && (
                  <Row className="pr-15p">
                    <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12}>
                      <p className="text-left mb-0 package-desc" style={{ fontSize: '10px', color: '#555', fontStyle: 'italic' }}>
                        (Pro-rated for remaining {pricing.remainingWeeks} {pricing.remainingWeeks === 1 ? 'week' : 'weeks'} of rental)
                      </p>
                    </Col>
                  </Row>
                )}
              </>
            ) : (
              <p className="text-left mb-1 package-desc">No boxes selected</p>
            )}
          </div>
        </Col>
      </Row>

      <Row className="ml-0 mr-0">
        <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12}>
          <div className="summary-panel-content pl-0 pr-0 order-package">
            <p className="text-left mb-1 fw-600 package-title">Packing Supplies</p>
            {packingSupplies.length === 0 ? (
              <p className="text-left mb-1 package-desc">No items in your cart</p>
            ) : (
              packingSupplies.map((item, index) => (
                <Row key={`packing_${index}`} className="mt-3 order-package-item align-items-center pr-15p">
                  <Col className="pl-1 pr-1" xxl={5} xl={5} lg={5} md={4} sm={5} xs={4}>
                    <div className="d-flex justify-content-between">
                      <Button
                        className="decrease-quanity d-flex align-items-center justify-content-center"
                        onClick={() => decreaseQuantity(index, 'PACKING')}
                        icon={<DeleteFilled />}
                        size={'medium'}
                      />
                      <p className="item-quantity mb-0 d-flex align-items-center pb-0">{item.quantity}</p>
                      <Button
                        className="increase-quanity d-flex align-items-center justify-content-center"
                        onClick={() => increaseQuantity(index, 'PACKING')}
                        icon={<PlusOutlined />}
                        size={'medium'}
                      />
                    </div>
                  </Col>
                  <Col className="pl-3 pr-1 fs-13 text-center" xxl={5} xl={5} lg={5} md={5} sm={5} xs={6}>
                    {item.title}
                  </Col>
                  <Col className="pl-1 pr-1 mt-0 text-center" xxl={2} xl={2} lg={2} md={2} sm={2} xs={2}>
                    ${(item.price * item.quantity).toFixed(2)}
                  </Col>
                </Row>
              ))
            )}
          </div>
        </Col>
      </Row>

      <Row className="ml-0 mr-0">
        <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12}>
          <div className="summary-panel-content pl-0 pr-0 order-package">
            <p className="text-left mb-1 fw-600 package-title">Moving Supplies</p>
            {movingSupplies.length === 0 ? (
              <p className="text-left mb-1 package-desc">No items in your cart</p>
            ) : (
              <>
                {movingSupplies.map((item, index) => (
                  <Row key={`moving_${index}`} className="mt-3 order-package-item align-items-center pr-15p">
                    <Col className="pl-1 pr-1" xxl={5} xl={5} lg={5} md={4} sm={5} xs={4}>
                      <div className="d-flex justify-content-between">
                        <Button
                          className="decrease-quanity d-flex align-items-center justify-content-center"
                          onClick={() => decreaseQuantity(index, 'MOVING')}
                          icon={<DeleteFilled />}
                          size={'medium'}
                        />
                        <p className="item-quantity mb-0 d-flex align-items-center pb-0">{item.quantity}</p>
                        <Button
                          className="increase-quanity d-flex align-items-center justify-content-center"
                          onClick={() => increaseQuantity(index, 'MOVING')}
                          icon={<PlusOutlined />}
                          size={'medium'}
                        />
                      </div>
                    </Col>
                    <Col className="pl-3 pr-1 fs-13 text-center" xxl={5} xl={5} lg={5} md={5} sm={5} xs={6}>
                      <div>
                        {item.title}
                        {pricing.remainingWeeks > 0 && (
                          <span style={{ fontSize: '10px', display: 'block', color: '#666', marginTop: '2px' }}>
                            ${item.price.toFixed(2)}/week × {pricing.remainingWeeks} {pricing.remainingWeeks === 1 ? 'week' : 'weeks'}
                          </span>
                        )}
                      </div>
                    </Col>
                    <Col className="pl-1 pr-1 mt-0 text-center" xxl={2} xl={2} lg={2} md={2} sm={2} xs={2}>
                      ${(item.price * item.quantity * (pricing.remainingWeeks || 0)).toFixed(2) || '0.00'}
                    </Col>
                  </Row>
                ))}
                {pricing.remainingWeeks > 0 && movingSupplies.length > 0 && (
                  <Row className="pr-15p mt-2">
                    <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12}>
                      <p className="text-left mb-0 package-desc" style={{ fontSize: '10px', color: '#555', fontStyle: 'italic' }}>
                        (Pro-rated for remaining {pricing.remainingWeeks} {pricing.remainingWeeks === 1 ? 'week' : 'weeks'} of rental)
                      </p>
                    </Col>
                  </Row>
                )}
              </>
            )}
          </div>
        </Col>
      </Row>

      {deliveryDetails.delivery_date && (
        <Row className="ml-0 mr-0 highlight-package">
          <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12}>
            <div className="summary-panel-content pl-0 pr-0 order-package">
              <p className="text-left mb-1 fw-600 package-title" key="delivery">
                Delivery Date & Time
              </p>
              <p className="mb-0">
                {moment(deliveryDetails.delivery_date).format('MMM DD, YYYY (dddd)')}
                <br />
                {(() => {
                  const [firstWindowId, secondWindowId] = getSortedWindows(
                    deliveryDetails.delivery_window_1_id,
                    deliveryDetails.delivery_window_2_id
                  );
                  return (
                    <>
                      {getWindowDisplay(firstWindowId)}
                      <br />
                      {getWindowDisplay(secondWindowId)}
                    </>
                  );
                })()}
              </p>
            </div>
          </Col>
        </Row>
      )}

      {pickupDetails.pickup_date && (
        <Row className="ml-0 mr-0 highlight-package">
          <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12}>
            <div className="summary-panel-content pl-0 pr-0 order-package">
              <p className="text-left mb-1 fw-600 package-title" key="pickup">
                Pickup Date & Time
              </p>
              <p className="mb-0">
                {moment(pickupDetails.pickup_date).format('MMM DD, YYYY (dddd)')}
                <br />
                {(() => {
                  const [firstWindowId, secondWindowId] = getSortedWindows(
                    pickupDetails.pickup_window_1_id,
                    pickupDetails.pickup_window_2_id
                  );
                  return (
                    <>
                      {getWindowDisplay(firstWindowId)}
                      <br />
                      {getWindowDisplay(secondWindowId)}
                    </>
                  );
                })()}
              </p>
            </div>
          </Col>
        </Row>
      )}

      {pricing.extraWorkPrice > 0 && (
        <Row className="ml-0 mr-0">
          <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12}>
            <div className="summary-panel-content pl-0 pr-0 order-package">
              <Row className="pr-15p">
                <Col xxl={9} xl={9} lg={9} md={9} sm={9} xs={9}>
                  <p className="text-left mb-1 package-desc">
                    Stair/Elevator
                  </p>
                </Col>
                <Col xxl={3} xl={3} lg={3} md={3} sm={3} xs={3}>
                  <p className="text-left mb-1 package-desc">
                    ${pricing?.extraWorkPrice?.toFixed(2) || '0.00'}
                  </p>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      )}

      {(boxCount > 0 || packingSupplies.length > 0 || movingSupplies.length > 0) && (
        <>
          <Row className="ml-0 mr-0">
            <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12}>
              <div className="summary-panel-content pl-0 pr-0 order-package">
                <Row className="pr-15p">
                  <Col xxl={9} xl={9} lg={9} md={9} sm={9} xs={9}>
                    <p className="text-left mb-1 package-desc">Transportation Fee</p>
                  </Col>
                  <Col xxl={3} xl={3} lg={3} md={3} sm={3} xs={3}>
                    <p className="text-left mb-1 package-desc">
                      ${pricing?.transportationFee?.toFixed(2) || '0.00'}
                    </p>
                  </Col>
                </Row>
                <Row className="pr-15p">
                  <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12}>
                    <p className="text-left mb-0 package-desc" style={{ fontSize: '10px', color: '#000' }}>
                      (Note: A flat $39 transportation fee applies for à la carte orders)
                    </p>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          <Row className="ml-0 mr-0">
            <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12}>
              <div className="summary-panel-content pl-0 pr-0 order-package">
                <Row className="pr-15p">
                  <Col xxl={9} xl={9} lg={9} md={9} sm={9} xs={9}>
                    <p className="text-left mb-1 package-desc">Sales Tax</p>
                  </Col>
                  <Col xxl={3} xl={3} lg={3} md={3} sm={3} xs={3}>
                    <p className="text-left mb-1 package-desc">
                      ${pricing?.tax?.toFixed(2) || '0.00'}
                    </p>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </>
      )}

      <div className="summary-footer bg-theme-secondary mb-0 d-flex">
        <div className="col-6 fw-600">Total Cost</div>
        <div className="col-6 fw-600 text-right">
          ${pricing?.total?.toFixed(2) || '0.00'}
        </div>
      </div>
    </Card>
  );
};

OrderSummary.propTypes = {
  deliveryWindows: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    display_value: PropTypes.string
  }))
};

OrderSummary.defaultProps = {
  deliveryWindows: []
};

export default OrderSummary;
